import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import Navbar from '../components/Layout/Navbar';
import Footerpage from '../components/Layout/Footer';
import StudentProjects from '../components/Sections/StudentProjects';
import TestimonialsSection from '../components/Sections/Testimonials';
import { useCourses } from '../context/CoursesContext';
import CourseMarquee from './CoursesMarquee';
import vishalAvatar from '../assets/intern-testimonials/vishal-avatar.jpg';
import sauravAvatar from '../assets/intern-testimonials/saurav-avatar.jpg';
import siddheshAvatar from '../assets/intern-testimonials/siddhesh-avatar.jpg';
import internshipVideoBanner from '../assets/internship-video-banner.jpeg';
import './InternshipPage.css';

// Banner imports - using plain photo banners from Courses_Banner folder
import AIML_Banner from '../assets/Courses_Banner/AI-banner.jpg';
import FullStack_Banner from '../assets/Courses_Banner/Frontend-Developments-banner.jpg';
import DATA_Analyst from '../assets/Courses_Banner/DATA-Analyst-banner.jpg';
import DATA_Science from '../assets/Courses_Banner/DATA-Analyst-banner2.jpg';
import DevOps_Banner from '../assets/Courses_Banner/DevOps-Engineer-banner.jpg';
import UI_UXbanner from '../assets/Courses_Banner/UI_UX-banner.jpg';

// Smart banner selection function (same logic as Courses.jsx)
const getFinalBanner = (title) => {
  const t = (title || '').toLowerCase();
  
  if (t.includes('ui') || t.includes('ux') || t.includes('design'))
    return UI_UXbanner;
  if (t.includes('devops'))
    return DevOps_Banner;
  if (t.includes('data science'))
    return DATA_Science;
  if (t.includes('data') || t.includes('analytics'))
    return DATA_Analyst;
  if (t.includes('ai') || t.includes('ml') || t.includes('machine'))
    return AIML_Banner;
  if (t.includes('web') || t.includes('full stack') || t.includes('fullstack'))
    return FullStack_Banner;
  
  return FullStack_Banner;
};

const PAYMENT_URL = 'https://rzp.io/rzp/weintern-internship';

const INTERNSHIP_DATA = {
  '3-month': {
    title: '3 & 6 Month Internship',
    subtitle: 'Learn. Build. Work on Real Projects. Get Career-Ready.',
    description: 'A 3-month industry-focused internship designed to help students move beyond theoretical learning and gain practical experience by working on real-world projects.',
    duration: '3 & 6 Months',
    registrationFee: '₹999',
    stipend: 'Up to ₹10,000',
    badge: 'Career-Ready',
    hero: {
      title: 'Learn. Build. Work on Real Projects. Get Career-Ready.',
      subtitle: 'Build your skills, strengthen your professional profile, and prepare yourself for the next step in your career.',
    },
    features: [
      { icon: 'mdi:briefcase-check', title: '100% Placement Support', desc: 'Dedicated career and placement support throughout your journey' },
      { icon: 'mdi:certificate-outline', title: 'Internship Certificate & LOR', desc: 'Official certificate and Letter of Recommendation' },
      { icon: 'mdi:file-document-edit', title: 'Professional Resume Building', desc: 'Industry-ready resume highlighting your skills and experience' },
      { icon: 'mdi:linkedin', title: 'LinkedIn Profile Optimization', desc: 'Build strong professional presence with profile guidance' },
      { icon: 'mdi:account-tie', title: 'Full-Time Employment', desc: 'Top 3 performers may be considered for full-time positions' },
      { icon: 'mdi:code-braces', title: 'Real Client Projects', desc: 'Work on real-world client projects with professional teams' },
      { icon: 'mdi:cash-multiple', title: 'Earn Through Contribution', desc: 'Up to 75% project value sharing based on your work' },
      { icon: 'mdi:domain', title: 'Industry Connections', desc: '40+ companies and industry partners network' },
    ],
    highlights: [
      'Duration: 3 & 6 Months',
      'Real-World Project Experience',
      '100% Placement Support',
      'Internship Certificate',
      'Letter of Recommendation',
      'Professional Resume Building',
      'LinkedIn Profile Optimization',
      'Performance-Based Full-Time Opportunities',
      'Up to 75% Project Value Sharing',
      '40+ Industry Connections',
      'Stipend Opportunities up to ₹10,000',
    ],
    whatYouGet: [
      {
        title: '100% Placement Support',
        desc: 'Get dedicated career and placement support throughout your internship journey to help you prepare for opportunities and confidently enter the job market.',
      },
      {
        title: 'Internship Certificate & Letter of Recommendation',
        desc: 'Receive an official internship certificate upon successful completion, along with a Letter of Recommendation based on your performance and contribution.',
      },
      {
        title: 'Professional Resume Building',
        desc: 'Create a professional, industry-ready resume that highlights your technical skills, project experience, achievements, and internship experience.',
      },
      {
        title: 'LinkedIn Profile Optimization',
        desc: 'Build a strong professional presence on LinkedIn with guidance on profile optimization, project showcasing, skills, and personal branding.',
      },
      {
        title: 'Opportunity for Full-Time Employment',
        desc: 'Your performance can open the door to a full-time career opportunity. The top 3 performing interns may be considered for a full-time position based on their performance, skills, and contribution during the internship.',
      },
    ],
    realProjects: {
      title: 'Work on Real Client Projects',
      subtitle: 'This isn\'t just a learning program.',
      desc: 'You get the opportunity to work on real-world client projects and experience how professional development teams operate.',
      earning: 'When you contribute to a client project, up to 75% of the project value generated through your contribution can be shared with the student/intern, giving you the opportunity to gain practical experience while earning based on your work.',
    },
    industryConnections: 'Get exposure to an ecosystem connected with 40+ companies and industry partners, creating opportunities to understand real industry requirements and explore potential career paths.',
  },
};

const InternshipPage = () => {
  const navigate = useNavigate();
  const { activeCourses } = useCourses();
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: '',
    college: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState('');
  const [openFAQ, setOpenFAQ] = useState(null);
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0); // Track which testimonial is in center

  // Testimonial data array
  const testimonials = [
    {
      id: 1,
      name: 'Vishal',
      role: 'Full Stack Developer',
      avatar: vishalAvatar,
      text: 'I started my journey as a free project management intern and, through consistent performance and learning, earned the opportunity to convert into a paid intern. Managing real projects helped me grow professionally.',
    },
    {
      id: 2,
      name: 'Saurav',
      role: 'Full Stack Developer',
      avatar: sauravAvatar,
      text: 'I started as a free intern and ended up getting a paid internship! WeIntern guided me step-by-step into Data Science and helped me land real projects that boosted my career.',
    },
    {
      id: 3,
      name: 'Siddhesh',
      role: 'Full Stack Developer',
      avatar: siddheshAvatar,
      text: 'I began as a free intern and gradually earned a paid web development internship by delivering tasks on time. This experience gave me hands-on exposure to real projects and professional workflows.',
    },
  ];

  const type = '3-month';
  const internship = INTERNSHIP_DATA[type];

  // Testimonial carousel navigation
  const handlePrevTestimonial = () => {
    setCurrentTestimonialIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const handleNextTestimonial = () => {
    setCurrentTestimonialIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  // Get reordered testimonials for carousel display
  const getOrderedTestimonials = () => {
    const total = testimonials.length;
    const ordered = [];
    
    // Previous card (left)
    const prevIndex = currentTestimonialIndex === 0 ? total - 1 : currentTestimonialIndex - 1;
    ordered.push({ ...testimonials[prevIndex], position: 'left' });
    
    // Current card (center)
    ordered.push({ ...testimonials[currentTestimonialIndex], position: 'center' });
    
    // Next card (right)
    const nextIndex = currentTestimonialIndex === total - 1 ? 0 : currentTestimonialIndex + 1;
    ordered.push({ ...testimonials[nextIndex], position: 'right' });
    
    return ordered;
  };

  if (!internship) {
    return (
      <div className="ip-page">
        <Navbar />
        <div className="ip-notfound">
          <h2>Internship Not Found</h2>
          <button onClick={() => navigate('/')} className="ip-btn">Go Home</button>
        </div>
      </div>
    );
  }

  const handleApply = () => {
    window.open(PAYMENT_URL, '_blank', 'noopener,noreferrer');
  };

  const handlePlayVideo = (videoUrl) => {
    setCurrentVideo(videoUrl);
    setShowVideoModal(true);
  };

  const handleDownloadBrochure = () => {
    window.print();
  };

  const handleCloseVideo = () => {
    setShowVideoModal(false);
    setCurrentVideo('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      const generatedApplicationId = `WI-${Date.now()}`;
      setApplicationId(generatedApplicationId);
      setShowApplyModal(false);
      window.open(PAYMENT_URL, '_blank', 'noopener,noreferrer');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Application submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="ip-page">
      <Navbar />

      {/* ═══════════════ NEW HERO (matches screenshot) ═══════════════ */}
      <section className="ip-new-hero">
        <div className="ip-new-hero-inner">
          {/* LEFT CONTENT */}
          <div className="ip-new-hero-left">
            <div className="ip-dpiit-badge">
              <Icon icon="mdi:shield-check" width={16} />
              Registered Under <strong>DPIIT</strong>, Govt. of India
            </div>

            <h1 className="ip-new-title">
              Certified Live Project <span className="ip-green-text">Internship</span>
            </h1>

            <p className="ip-new-desc">
              Join our <strong>3 Month and 6 Month Internship</strong> and work on real industry projects
              with expert mentorship and certification. Top performing students will
              get a <strong>fulltime opportunity</strong>.
            </p>

            {/* Steps */}
            <div className="ip-steps">
              <div className="ip-step">
                <div className="ip-step-num">1</div>
                <div>
                  <strong>Book Slot</strong>
                  <span>Register for the program</span>
                </div>
              </div>
              <div className="ip-step-line"></div>
              <div className="ip-step">
                <div className="ip-step-num">2</div>
                <div>
                  <strong>Live Classes</strong>
                  <span>Month 1 – Expert mentors</span>
                </div>
              </div>
              <div className="ip-step-line"></div>
              <div className="ip-step">
                <div className="ip-step-num">3</div>
                <div>
                  <strong>Build Projects</strong>
                  <span>Month 2 – Real projects 🎯</span>
                </div>
              </div>
            </div>

            {/* Urgency */}
            <div className="ip-urgency">
              🔥 50 seats left at this price — batch filling fast!
            </div>

            {/* Buttons */}
            <div className="ip-hero-btns">
              <button className="ip-btn-primary" onClick={handleApply}>
                Enroll Now
              </button>
              <button className="ip-btn-outline" onClick={handleDownloadBrochure}>
                Download Brochure
              </button>
            </div>

            {/* Social Proof */}
            <div className="ip-social-proof">
              <div className="ip-proof-item">
                <div className="ip-avatars">
                  <img src="https://i.pravatar.cc/40?img=1" alt="" />
                  <img src="https://i.pravatar.cc/40?img=2" alt="" />
                  <img src="https://i.pravatar.cc/40?img=3" alt="" />
                  <img src="https://i.pravatar.cc/40?img=4" alt="" />
                </div>
                <div>
                  <strong>20,000+</strong>
                  <span>Total Learners</span>
                </div>
              </div>
              <div className="ip-proof-item">
                <div className="ip-proof-icon">
                  <Icon icon="mdi:account-group" width={28} />
                </div>
                <div>
                  <strong>100k+</strong>
                  <span>Growing Community</span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT CONTENT */}
          <div className="ip-new-hero-right">
            <div className="ip-hero-video-card" onClick={() => handlePlayVideo('https://www.youtube.com/embed/X2x8C8AQHyY')}>
              <img
                src={internshipVideoBanner}
                alt="Internship Program Video"
                className="ip-hero-video-thumbnail"
              />
              <div className="ip-hero-video-overlay">
                <div className="ip-hero-play-button">
                  <Icon icon="mdi:play" width={48} height={48} color="white" />
                </div>
                <div className="ip-hero-video-label">
                  <Icon icon="mdi:play-circle" width={20} height={20} />
                  <span>Watch Program Overview</span>
                </div>
              </div>
            </div>

            <div className="ip-feature-cards">
              <div className="ip-fcard">
                <div className="ip-fcard-icon blue">
                  <Icon icon="mdi:school" width={22} />
                </div>
                <div>
                  <h4>Certificate & Offer Letter</h4>
                  <p>After completing the program, receive an <strong>official certificate</strong> along with an <strong>internship offer letter</strong>.</p>
                </div>
              </div>

              <div className="ip-fcard">
                <div className="ip-fcard-icon orange">
                  <Icon icon="mdi:video" width={22} />
                </div>
                <div>
                  <h4>100% Live Interactive Classes</h4>
                  <p>Learn through <strong>live classes</strong>, real-time doubt clearing, and hands-on live projects guided by expert mentors.</p>
                </div>
              </div>

              <div className="ip-fcard">
                <div className="ip-fcard-icon purple">
                  <Icon icon="mdi:briefcase-check" width={22} />
                </div>
                <div>
                  <h4>Placement Support</h4>
                  <p>Get <strong>dedicated placement support</strong> with resume building, interview preparation, and job referrals to kickstart your career.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Verified By Section */}
      <section className="ip-verified-section">
        <div className="ip-container">
          <h2 className="ip-verified-title">
            Verified By <br className="mobile-break" />
            <span className="text-blue no-wrap-mobile">Government & Industry</span>
          </h2>
          <p className="ip-verified-subtitle">
            Recognitions that validate our commitment to quality, innovation & impact
          </p>
          
          <div className="ip-verified-slider">
            <div className="ip-verified-track">
              {/* First set of logos */}
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/nsdc.png" alt="NSDC" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/skill-india.png" alt="Skill India" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/dpiit.png" alt="DPIIT Startup India" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/aicte.png" alt="AICTE" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/skill-ministry.png" alt="Ministry of Skill Development" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/google-partner.png" alt="Google Partner" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/aws.png" alt="AWS" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/ibm.png" alt="IBM" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/iso.png" alt="ISO Certified" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/msme.png" alt="MSME" />
              </div>
              
              {/* Duplicate set for seamless loop */}
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/nsdc.png" alt="NSDC" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/skill-india.png" alt="Skill India" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/dpiit.png" alt="DPIIT Startup India" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/aicte.png" alt="AICTE" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/skill-ministry.png" alt="Ministry of Skill Development" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/google-partner.png" alt="Google Partner" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/aws.png" alt="AWS" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/ibm.png" alt="IBM" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/iso.png" alt="ISO Certified" />
              </div>
              <div className="ip-verified-logo-card">
                <img src="/trust-logos/msme.png" alt="MSME" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 100% Live Classes Section */}
      <section className="ip-live-classes-section">
        <div className="ip-container">
          <div className="ip-live-classes-badge">
            ⚡ Class Experience
          </div>
          
          <h2 className="ip-live-classes-title">
            100% <span className="text-blue">Live Classes</span>
          </h2>
          
          <p className="ip-live-classes-subtitle">
            No boring pre-recorded content. Every session, every day — always live with your mentor.
          </p>
          
          <div className="ip-live-classes-cards">
            {/* Card 1 - 100% Live Sessions */}
            <div className="ip-live-class-card">
              <div className="ip-card-top">
                <div className="ip-card-icon-wrapper red">
                  <Icon icon="mdi:video" width={28} />
                </div>
                <div className="ip-card-badge-live">LIVE</div>
              </div>
              
              <h3 className="ip-card-heading">
                100% Live Sessions Entire <span className="highlight-blue">2 Months</span>
              </h3>
              
              <p className="ip-card-description">
                Every session is live — no pre-recorded content. Learn in real-time with expert mentors.
              </p>
              
              <div className="ip-card-features-simple">
                <div className="ip-feature-simple">
                  <Icon icon="mdi:check-circle" width={16} />
                  <span>Real-time learning</span>
                </div>
                <div className="ip-feature-simple">
                  <Icon icon="mdi:check-circle" width={16} />
                  <span>Direct interaction</span>
                </div>
                <div className="ip-feature-simple">
                  <Icon icon="mdi:check-circle" width={16} />
                  <span>Instant doubt clearing</span>
                </div>
              </div>
            </div>
            
            {/* Card 2 - Recording Shared */}
            <div className="ip-live-class-card">
              <div className="ip-card-top">
                <div className="ip-card-icon-wrapper blue">
                  <Icon icon="mdi:video-box" width={28} />
                </div>
              </div>
              
              <h3 className="ip-card-heading">
                Recording Shared After <span className="highlight-blue">Every Class</span>
              </h3>
              
              <p className="ip-card-description">
                Missed a session? Full recordings sent after each class for lifetime access.
              </p>
              
              <div className="ip-card-features-simple">
                <div className="ip-feature-simple">
                  <Icon icon="mdi:check-circle" width={16} />
                  <span>Lifetime access</span>
                </div>
                <div className="ip-feature-simple">
                  <Icon icon="mdi:check-circle" width={16} />
                  <span>Download & rewatch</span>
                </div>
                <div className="ip-feature-simple">
                  <Icon icon="mdi:check-circle" width={16} />
                  <span>Any device, anytime</span>
                </div>
              </div>
            </div>
            
            {/* Card 3 - All Classes in Evening */}
            <div className="ip-live-class-card">
              <div className="ip-card-top">
                <div className="ip-card-icon-wrapper blue">
                  <Icon icon="mdi:clock-time-eight" width={28} />
                </div>
              </div>
              
              <h3 className="ip-card-heading">
                All Classes <span className="highlight-blue">in the Evening</span>
              </h3>
              
              <p className="ip-card-description">
                Classes scheduled after college hours — no conflicts with your regular studies.
              </p>
              
              <div className="ip-card-features-simple">
                <div className="ip-feature-simple">
                  <Icon icon="mdi:check-circle" width={16} />
                  <span>No college conflict</span>
                </div>
                <div className="ip-feature-simple">
                  <Icon icon="mdi:check-circle" width={16} />
                  <span>Evening sessions</span>
                </div>
                <div className="ip-feature-simple">
                  <Icon icon="mdi:check-circle" width={16} />
                  <span>Flexible schedule</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pick Your Internship Domain Section */}
      <section className="ip-domains-section">
        <div className="ip-container">
          <h2 className="ip-domains-title">
            Pick Your <span className="text-blue">Internship Domain</span>
          </h2>
          <p className="ip-domains-subtitle">
            6 domains available. Pick one and get your offer letter, certificate & report card.
          </p>
          
          <div className="ip-domains-grid">
            {/* AI/ML Internship */}
            <div className="ip-domain-card" onClick={() => window.open(PAYMENT_URL, '_blank')}>
              <div className="ip-card-image">
                <img src={getFinalBanner('AI / ML Internship')} alt="AI/ML Internship" />
              </div>
              <div className="ip-card-content">
                <div className="ip-card-provider">
                  <Icon icon="lucide:brain-circuit" width={14} height={14} />
                  <span>WeIntern</span>
                </div>
                <h3 className="ip-card-title">AI / ML 2 Months Internship</h3>
                <div className="ip-card-benefits">
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:badge-check" width={13} height={13} />
                    Certificates
                  </span>
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:briefcase" width={13} height={13} />
                    Free Internships
                  </span>
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:trophy" width={13} height={13} />
                    100% Placement
                  </span>
                </div>
                <button className="ip-explore-btn" onClick={(e) => { e.stopPropagation(); window.open(PAYMENT_URL, '_blank'); }}>
                  Explore Now
                  <Icon icon="lucide:arrow-right" width={15} height={15} />
                </button>
              </div>
            </div>

            {/* Full Stack Web Development */}
            <div className="ip-domain-card" onClick={() => window.open(PAYMENT_URL, '_blank')}>
              <div className="ip-card-image">
                <img src={getFinalBanner('Web Development Internship')} alt="Web Development Internship" />
              </div>
              <div className="ip-card-content">
                <div className="ip-card-provider">
                  <Icon icon="lucide:code-2" width={14} height={14} />
                  <span>WeIntern</span>
                </div>
                <h3 className="ip-card-title">Web Dev 2 Months Internship</h3>
                <div className="ip-card-benefits">
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:badge-check" width={13} height={13} />
                    Certificates
                  </span>
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:briefcase" width={13} height={13} />
                    Free Internships
                  </span>
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:trophy" width={13} height={13} />
                    100% Placement
                  </span>
                </div>
                <button className="ip-explore-btn" onClick={(e) => { e.stopPropagation(); window.open(PAYMENT_URL, '_blank'); }}>
                  Explore Now
                  <Icon icon="lucide:arrow-right" width={15} height={15} />
                </button>
              </div>
            </div>

            {/* Data Analytics */}
            <div className="ip-domain-card" onClick={() => window.open(PAYMENT_URL, '_blank')}>
              <div className="ip-card-image">
                <img src={getFinalBanner('Data Analytics Internship')} alt="Data Analytics Internship" />
              </div>
              <div className="ip-card-content">
                <div className="ip-card-provider">
                  <Icon icon="lucide:database" width={14} height={14} />
                  <span>WeIntern</span>
                </div>
                <h3 className="ip-card-title">Data Analytics Internship</h3>
                <div className="ip-card-benefits">
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:badge-check" width={13} height={13} />
                    Certificates
                  </span>
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:briefcase" width={13} height={13} />
                    Free Internships
                  </span>
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:trophy" width={13} height={13} />
                    100% Placement
                  </span>
                </div>
                <button className="ip-explore-btn" onClick={(e) => { e.stopPropagation(); window.open(PAYMENT_URL, '_blank'); }}>
                  Explore Now
                  <Icon icon="lucide:arrow-right" width={15} height={15} />
                </button>
              </div>
            </div>

            {/* Data Science */}
            <div className="ip-domain-card" onClick={() => window.open(PAYMENT_URL, '_blank')}>
              <div className="ip-card-image">
                <img src={getFinalBanner('Data Science Internship')} alt="Data Science Internship" />
              </div>
              <div className="ip-card-content">
                <div className="ip-card-provider">
                  <Icon icon="lucide:database" width={14} height={14} />
                  <span>WeIntern</span>
                </div>
                <h3 className="ip-card-title">Data Science Internship</h3>
                <div className="ip-card-benefits">
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:badge-check" width={13} height={13} />
                    Certificates
                  </span>
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:briefcase" width={13} height={13} />
                    Free Internships
                  </span>
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:trophy" width={13} height={13} />
                    100% Placement
                  </span>
                </div>
                <button className="ip-explore-btn" onClick={(e) => { e.stopPropagation(); window.open(PAYMENT_URL, '_blank'); }}>
                  Explore Now
                  <Icon icon="lucide:arrow-right" width={15} height={15} />
                </button>
              </div>
            </div>

            {/* DevOps */}
            <div className="ip-domain-card" onClick={() => window.open(PAYMENT_URL, '_blank')}>
              <div className="ip-card-image">
                <img src={getFinalBanner('DevOps Internship')} alt="DevOps Internship" />
              </div>
              <div className="ip-card-content">
                <div className="ip-card-provider">
                  <Icon icon="lucide:cloud" width={14} height={14} />
                  <span>WeIntern</span>
                </div>
                <h3 className="ip-card-title">DevOps Internship</h3>
                <div className="ip-card-benefits">
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:badge-check" width={13} height={13} />
                    Certificates
                  </span>
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:briefcase" width={13} height={13} />
                    Free Internships
                  </span>
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:trophy" width={13} height={13} />
                    100% Placement
                  </span>
                </div>
                <button className="ip-explore-btn" onClick={(e) => { e.stopPropagation(); window.open(PAYMENT_URL, '_blank'); }}>
                  Explore Now
                  <Icon icon="lucide:arrow-right" width={15} height={15} />
                </button>
              </div>
            </div>

            {/* UI/UX Design */}
            <div className="ip-domain-card" onClick={() => window.open(PAYMENT_URL, '_blank')}>
              <div className="ip-card-image">
                <img src={getFinalBanner('UI/UX Design Internship')} alt="UI/UX Design Internship" />
              </div>
              <div className="ip-card-content">
                <div className="ip-card-provider">
                  <Icon icon="lucide:laptop" width={14} height={14} />
                  <span>WeIntern</span>
                </div>
                <h3 className="ip-card-title">UI/UX Design Internship</h3>
                <div className="ip-card-benefits">
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:badge-check" width={13} height={13} />
                    Certificates
                  </span>
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:briefcase" width={13} height={13} />
                    Free Internships
                  </span>
                  <span className="ip-benefit-chip">
                    <Icon icon="lucide:trophy" width={13} height={13} />
                    100% Placement
                  </span>
                </div>
                <button className="ip-explore-btn" onClick={(e) => { e.stopPropagation(); window.open(PAYMENT_URL, '_blank'); }}>
                  Explore Now
                  <Icon icon="lucide:arrow-right" width={15} height={15} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* From Free Intern to Paid Intern Section */}
      <section className="ip-testimonial-section">
        <div className="ip-container">
          <h2 className="ip-testimonial-title">
            From Free Intern to <span className="text-blue">Paid Intern</span>
          </h2>
          <p className="ip-testimonial-subtitle">
            Real growth stories from our interns 🚀
          </p>
          
          <div className="ip-testimonial-carousel-container">
            {/* Left Arrow */}
            <button 
              className="ip-testimonial-nav-arrow ip-testimonial-nav-left"
              onClick={handlePrevTestimonial}
              aria-label="Previous testimonial"
            >
              <Icon icon="mdi:chevron-left" width={32} height={32} />
            </button>

            <div className="ip-testimonial-cards-wrapper">
              {getOrderedTestimonials().map((testimonial, index) => (
                <div 
                  key={`${testimonial.id}-${index}`}
                  className={`ip-testimonial-simple-card ${testimonial.position === 'center' ? 'ip-testimonial-card-highlight' : ''}`}
                >
                  <div className="ip-testimonial-quote-icon">"</div>
                  <div className="ip-testimonial-avatar-wrapper">
                    <img 
                      src={testimonial.avatar} 
                      alt={`${testimonial.name} testimonial`}
                      className="ip-testimonial-avatar-circle"
                    />
                  </div>
                  <p className="ip-testimonial-text">
                    {testimonial.text}
                  </p>
                  <div className="ip-testimonial-stars">
                    <Icon icon="mdi:star" width={18} height={18} color="#fbbf24" />
                    <Icon icon="mdi:star" width={18} height={18} color="#fbbf24" />
                    <Icon icon="mdi:star" width={18} height={18} color="#fbbf24" />
                    <Icon icon="mdi:star" width={18} height={18} color="#fbbf24" />
                    <Icon icon="mdi:star" width={18} height={18} color="#fbbf24" />
                  </div>
                  <h4 className="ip-testimonial-name">{testimonial.name}</h4>
                  <p className="ip-testimonial-role">{testimonial.role}</p>
                </div>
              ))}
            </div>

            {/* Right Arrow */}
            <button 
              className="ip-testimonial-nav-arrow ip-testimonial-nav-right"
              onClick={handleNextTestimonial}
              aria-label="Next testimonial"
            >
              <Icon icon="mdi:chevron-right" width={32} height={32} />
            </button>
          </div>
        </div>
      </section>

      {/* Real Student Reviews - Using Home Page Component */}
      <TestimonialsSection />

      <StudentProjects />

      {/* FAQ Section */}
      <section className="ip-faq-section">
        <div className="ip-container">
          <div className="ip-faq-badge">
            💡 Got Questions?
          </div>
          
          <h2 className="ip-faq-title">
            Weintern 3-Month Internship – <span style={{ color: '#2563eb' }}>FAQs</span>
          </h2>
          <p className="ip-faq-subtitle">
            All you need to know about our internship, projects, benefits & certification.
          </p>

          <div className="ip-faq-list">
            {/* FAQ 1 */}
            <div className="ip-faq-item">
              <button 
                className={`ip-faq-question ${openFAQ === 1 ? 'active' : ''}`}
                onClick={() => setOpenFAQ(openFAQ === 1 ? null : 1)}
              >
                <Icon icon="mdi:lightbulb-outline" width={22} className="ip-faq-icon" />
                <span>What is the 2-Month Internship Program?</span>
                <Icon icon={openFAQ === 1 ? "mdi:chevron-up" : "mdi:chevron-down"} width={24} className="ip-faq-arrow" />
              </button>
              {openFAQ === 1 && (
                <div className="ip-faq-answer">
                  <p>The 2-Month Internship Program is a structured, live learning experience where students work on real-world projects under expert mentorship. Month 1 focuses on live classes and skill-building, while Month 2 involves hands-on project work with industry-standard tools and practices.</p>
                </div>
              )}
            </div>

            {/* FAQ 2 */}
            <div className="ip-faq-item">
              <button 
                className={`ip-faq-question ${openFAQ === 2 ? 'active' : ''}`}
                onClick={() => setOpenFAQ(openFAQ === 2 ? null : 2)}
              >
                <Icon icon="mdi:account-group-outline" width={22} className="ip-faq-icon" />
                <span>Who can join this internship?</span>
                <Icon icon={openFAQ === 2 ? "mdi:chevron-up" : "mdi:chevron-down"} width={24} className="ip-faq-arrow" />
              </button>
              {openFAQ === 2 && (
                <div className="ip-faq-answer">
                  <p>This internship is open to college students, recent graduates, and anyone looking to gain practical industry experience. Whether you're a beginner or have some foundational knowledge, our program is designed to help you grow and build real-world skills.</p>
                </div>
              )}
            </div>

            {/* FAQ 3 */}
            <div className="ip-faq-item">
              <button 
                className={`ip-faq-question ${openFAQ === 3 ? 'active' : ''}`}
                onClick={() => setOpenFAQ(openFAQ === 3 ? null : 3)}
              >
                <Icon icon="mdi:school-outline" width={22} className="ip-faq-icon" />
                <span>Which domains are available right now?</span>
                <Icon icon={openFAQ === 3 ? "mdi:chevron-up" : "mdi:chevron-down"} width={24} className="ip-faq-arrow" />
              </button>
              {openFAQ === 3 && (
                <div className="ip-faq-answer">
                  <p>We offer internships in 6 domains: AI/ML, Web Development, Data Analytics, Data Science, DevOps, and UI/UX Design. Each domain includes live classes, real projects, certification, and placement support.</p>
                </div>
              )}
            </div>

            {/* FAQ 4 */}
            <div className="ip-faq-item">
              <button 
                className={`ip-faq-question ${openFAQ === 4 ? 'active' : ''}`}
                onClick={() => setOpenFAQ(openFAQ === 4 ? null : 4)}
              >
                <Icon icon="mdi:file-document-outline" width={22} className="ip-faq-icon" />
                <span>How is the internship structured?</span>
                <Icon icon={openFAQ === 4 ? "mdi:chevron-up" : "mdi:chevron-down"} width={24} className="ip-faq-arrow" />
              </button>
              {openFAQ === 4 && (
                <div className="ip-faq-answer">
                  <p><strong>Month 1:</strong> Live interactive classes with expert mentors covering fundamentals and advanced concepts in your chosen domain. All classes are conducted in the evening to avoid college hour clashes.</p>
                  <p><strong>Month 2:</strong> Hands-on project work where you build real applications, receive feedback, and develop a professional portfolio.</p>
                </div>
              )}
            </div>

            {/* FAQ 5 */}
            <div className="ip-faq-item">
              <button 
                className={`ip-faq-question ${openFAQ === 5 ? 'active' : ''}`}
                onClick={() => setOpenFAQ(openFAQ === 5 ? null : 5)}
              >
                <Icon icon="mdi:briefcase-outline" width={22} className="ip-faq-icon" />
                <span>Will I get job or paid internship opportunities after completion?</span>
                <Icon icon={openFAQ === 5 ? "mdi:chevron-up" : "mdi:chevron-down"} width={24} className="ip-faq-arrow" />
              </button>
              {openFAQ === 5 && (
                <div className="ip-faq-answer">
                  <p>Yes! We provide 100% placement support including resume building, interview preparation, and job referrals. Top performers may be considered for paid internship or full-time opportunities. We're connected with 40+ industry partners to help you find the right opportunity.</p>
                </div>
              )}
            </div>

            {/* FAQ 6 */}
            <div className="ip-faq-item">
              <button 
                className={`ip-faq-question ${openFAQ === 6 ? 'active' : ''}`}
                onClick={() => setOpenFAQ(openFAQ === 6 ? null : 6)}
              >
                <Icon icon="mdi:currency-inr" width={22} className="ip-faq-icon" />
                <span>Is the internship free or paid?</span>
                <Icon icon={openFAQ === 6 ? "mdi:chevron-up" : "mdi:chevron-down"} width={24} className="ip-faq-arrow" />
              </button>
              {openFAQ === 6 && (
                <div className="ip-faq-answer">
                  <p>The slot booking fee is ₹999, which is 100% refundable. There are no hidden charges. This fee secures your seat in the program and demonstrates your commitment. Upon successful completion, you'll receive certification, project experience, and placement support at no additional cost.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footerpage />

      {/* Video Modal */}
      {showVideoModal && (
        <div className="ip-video-modal-overlay" onClick={handleCloseVideo}>
          <div className="ip-video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="ip-video-modal-close" onClick={handleCloseVideo}>
              <Icon icon="mdi:close" width={24} />
            </button>
            <div className="ip-video-wrapper">
              <iframe
                src={currentVideo}
                title="Testimonial Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InternshipPage;